import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { get, isArray, isNumber, pick, values } from 'lodash';
import { createSelector } from 'reselect';

import { Result, ResultPaginated } from '../reducers/createResultReducer';
import { Resource } from '../resources/createResource';
import { EntityBase } from '../resources/types';

export interface UseSelectEntitiesDataByResultArgs<E extends EntityBase> {
  resource: Resource<E>;
  result?: Result;
}

const makeSelectDataByResult = () =>
  createSelector(
    (state: unknown, { resource }: UseSelectEntitiesDataByResultArgs<any>) => resource.selectEntitiesObj(state),
    (_: any, { result }: UseSelectEntitiesDataByResultArgs<any>) => result,
    (obj, result) => {
      // Handle paginated results
      const page = get(result, 'page');
      if (isNumber(page)) {
        const resultPaginated = result as ResultPaginated;
        return {
          ...resultPaginated,
          response: values(pick(obj, resultPaginated.response)),
        };
      }
      // Handle lists and single items
      const value = get(result, 'response', result);
      return isArray(value) ? values(pick(obj, value)) : obj[value];
    },
  );

export default function useSelectEntitiesDataByResult<E extends EntityBase>({
  resource,
  result,
}: UseSelectEntitiesDataByResultArgs<E>) {
  // Return the data using a selector in order to keep it updated
  const select = useMemo(makeSelectDataByResult, []);
  const data = useSelector((state) => select(state, { resource, result }));

  return data;
}
